import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { classesApi, type ClassResponse, type ClassExportResponse } from '@/api/endpoints/classes'
import { useUI } from '@/stores/ui'

interface Props {
  open: boolean
  onClose: () => void
  classes: ClassResponse[]
  onClassCreated?: () => void
}

type Mode = 'menu' | 'export' | 'import' | 'search'

export default function ExportImportClassesModal({ open, onClose, classes, onClassCreated }: Props) {
  const addToast = useUI((state) => state.addToast)
  const [mode, setMode] = useState<Mode>('menu')
  const [selectedClass, setSelectedClass] = useState<ClassResponse | null>(null)
  const [exporting, setExporting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [targetStartDate, setTargetStartDate] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchBy, setSearchBy] = useState<'name' | 'id' | 'teacher'>('name')
  const [loadingExport, setLoadingExport] = useState<string | null>(null) // Track which class is being exported

  const handleExport = async () => {
    if (!selectedClass) return
    setExporting(true)
    try {
      const exported = await classesApi.export(selectedClass.id)
      
      // Create blob and download
      const blob = new Blob([JSON.stringify(exported, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${exported.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${exported.class_id}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      
      addToast({ message: 'Class exported successfully', type: 'success' })
      setMode('menu')
      setSelectedClass(null)
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to export class',
        type: 'error',
      })
    } finally {
      setExporting(false)
    }
  }

  const handleImport = async (classJson: ClassExportResponse) => {
    if (!targetStartDate) {
      addToast({ message: 'Please select a start date', type: 'error' })
      return
    }

    setImporting(true)
    try {
      await classesApi.import(classJson, targetStartDate)
      addToast({ message: 'Class imported successfully', type: 'success' })
      onClassCreated?.()
      setMode('menu')
      setTargetStartDate('')
      setSelectedClass(null)
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to import class',
        type: 'error',
      })
    } finally {
      setImporting(false)
    }
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const classJson = JSON.parse(e.target?.result as string) as ClassExportResponse
        setSelectedClass(classJson as any) // Use the imported data
      } catch (error) {
        addToast({ message: 'Invalid file format', type: 'error' })
      }
    }
    reader.readAsText(file)
  }

  // Simple fuzzy search: prioritizes exact matches, then contains, then similar
  const filteredClasses = classes
    .map(c => {
      const q = searchQuery.trim().toLowerCase()
      if (!q) return { class: c, score: 1 }
      
      let score = 0
      
      if (searchBy === 'name') {
        const name = c.name.toLowerCase()
        if (name === q) score = 100 // Exact match
        else if (name.startsWith(q)) score = 50 // Starts with
        else if (name.includes(q)) score = 25 // Contains
        else if (name.split(' ').some(word => word.startsWith(q))) score = 10 // Word starts with
      } else if (searchBy === 'id') {
        const id = c.class_id?.toLowerCase() || ''
        if (id === q) score = 100
        else if (id.includes(q)) score = 50
      } else if (searchBy === 'teacher') {
        const matches = c.teacher_names?.some(t => {
          const teacher = t.toLowerCase()
          return teacher === q || teacher.startsWith(q) || teacher.includes(q)
        }) || false
        if (matches) score = 50
      }
      
      return { class: c, score }
    })
    .filter(item => item.score > 0 || !searchQuery.trim())
    .sort((a, b) => b.score - a.score)
    .map(item => item.class)

  if (!open) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 hover-raise max-h-[90vh] overflow-y-auto pointer-events-auto"
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
        >
          {mode === 'menu' && (
            <>
              <h2 className="text-xl font-semibold mb-4">Export / Import / Search Classes</h2>
              <div className="space-y-3">
                <button
                  className="w-full btn-primary text-left p-4 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  onClick={() => setMode('export')}
                  title="Export a class to a JSON file that you can save and import later or share with others. Includes all class details and events."
                >
                  📤 Export Class
                </button>
                <button
                  className="w-full btn-secondary text-left p-4 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  onClick={() => setMode('import')}
                  title="Import a class from a previously exported JSON file. You can specify a new start date and all events will be adjusted accordingly."
                >
                  📥 Import Class from File
                </button>
                <button
                  className="w-full btn-secondary text-left p-4 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  onClick={() => setMode('search')}
                  title="Search for classes from previous years to use as templates. Fuzzy search by name, ID, or teacher."
                >
                  🔍 Search Previous Classes
                </button>
              </div>
              <div className="mt-6 flex justify-end">
                <button className="btn-secondary" onClick={onClose}>Cancel</button>
              </div>
            </>
          )}

          {mode === 'export' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Export Class</h2>
                <button className="btn-secondary text-sm" onClick={() => { setMode('menu'); setSelectedClass(null) }}>← Back</button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Select Class to Export</label>
                  <select
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={selectedClass?.id || ''}
                    onChange={(e) => {
                      const cls = classes.find(c => c.id === e.target.value)
                      setSelectedClass(cls || null)
                    }}
                  >
                    <option value="">-- Select a class --</option>
                    {classes.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                {selectedClass && (
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm">
                      <div className="font-medium">{selectedClass.name}</div>
                      {selectedClass.class_id && (
                        <div className="text-gray-600">ID: {selectedClass.class_id}</div>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button className="btn-secondary" onClick={() => { setMode('menu'); setSelectedClass(null) }}>Cancel</button>
                  <button
                    className="btn-primary"
                    onClick={handleExport}
                    disabled={!selectedClass || exporting}
                    title="Download this class as a JSON file. You can later import it to create a new class with the same structure."
                  >
                    {exporting ? 'Exporting...' : 'Export Class'}
                  </button>
                </div>
              </div>
            </>
          )}

          {mode === 'import' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Import Class from File</h2>
                <button className="btn-secondary text-sm" onClick={() => { setMode('menu'); setSelectedClass(null); setTargetStartDate('') }}>← Back</button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Select File</label>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileUpload}
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  />
                </div>
                {selectedClass && (
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-sm">
                        <div className="font-medium">{(selectedClass as any).name || selectedClass.name}</div>
                        {(selectedClass as any).class_id && (
                          <div className="text-gray-600">Original ID: {(selectedClass as any).class_id}</div>
                        )}
                        {(selectedClass as any).events && Array.isArray((selectedClass as any).events) && (
                          <div className="text-gray-600 mt-1">
                            Contains {(selectedClass as any).events.length} event{(selectedClass as any).events.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">New Start Date *</label>
                      <input
                        type="date"
                        className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                        value={targetStartDate}
                        onChange={(e) => setTargetStartDate(e.target.value)}
                        required
                      />
                      {targetStartDate && (() => {
                        const date = new Date(targetStartDate)
                        const weekday = date.toLocaleDateString('en-US', { weekday: 'long' })
                        return (
                          <div className="text-xs text-gray-600 mt-1">
                            Selected: <strong>{weekday}</strong> - Events will be adjusted to start from this date, maintaining the same pattern. Holiday weeks will be skipped.
                          </div>
                        )
                      })()}
                      {!targetStartDate && (
                        <div className="text-xs text-gray-600 mt-1">
                          Events will be adjusted to start from this date, maintaining the same pattern. Holiday weeks will be skipped.
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <button className="btn-secondary" onClick={() => { setMode('menu'); setSelectedClass(null); setTargetStartDate('') }}>Cancel</button>
                      <button
                        className="btn-primary"
                        onClick={() => handleImport(selectedClass as any)}
                        disabled={!targetStartDate || importing}
                        title="Import this class with all its events. Events will be adjusted to start from the selected date while maintaining the same weekly pattern."
                      >
                        {importing ? 'Importing...' : 'Import Class'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {mode === 'search' && (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Search Previous Classes</h2>
                <button className="btn-secondary text-sm" onClick={() => { setMode('menu'); setSearchQuery(''); setSelectedClass(null); setTargetStartDate('') }}>← Back</button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Search By</label>
                  <select
                    className="input w-full mb-2 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={searchBy}
                    onChange={(e) => setSearchBy(e.target.value as any)}
                  >
                    <option value="name">Class Name</option>
                    <option value="id">Class ID</option>
                    <option value="teacher">Teacher</option>
                  </select>
                  <input
                    type="text"
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    placeholder={`Search by ${searchBy} (fuzzy search enabled)...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <div className="text-xs text-gray-500 mt-1">
                      Found {filteredClasses.length} class{filteredClasses.length !== 1 ? 'es' : ''} (sorted by relevance)
                    </div>
                  )}
                </div>
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                  {filteredClasses.length === 0 ? (
                    <div className="p-4 text-sm text-gray-500 text-center">No classes found</div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {filteredClasses.map(c => (
                        <button
                          key={c.id}
                          className="w-full p-3 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                          onClick={async () => {
                            setLoadingExport(c.id)
                            try {
                              // Auto-export and prepare for import
                              const exported = await classesApi.export(c.id)
                              setSelectedClass(exported as any)
                            } catch (error: any) {
                              addToast({
                                message: error.response?.data?.detail || 'Failed to load class details',
                                type: 'error',
                              })
                            } finally {
                              setLoadingExport(null)
                            }
                          }}
                          disabled={loadingExport === c.id}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium">{c.name}</div>
                              {c.class_id && (
                                <div className="text-xs text-gray-600">ID: {c.class_id}</div>
                              )}
                              {c.teacher_names && c.teacher_names.length > 0 && (
                                <div className="text-xs text-gray-600">Teachers: {c.teacher_names.join(', ')}</div>
                              )}
                            </div>
                            {loadingExport === c.id && (
                              <div className="text-xs text-gray-500 ml-2">Loading...</div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {selectedClass && (selectedClass as any).name && (
                  <div className="space-y-4 pt-4 border-t border-gray-200">
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-sm">
                        <div className="font-medium">{(selectedClass as any).name}</div>
                        {(selectedClass as any).class_id && (
                          <div className="text-gray-600">Original ID: {(selectedClass as any).class_id}</div>
                        )}
                        {(selectedClass as any).events && Array.isArray((selectedClass as any).events) && (
                          <div className="text-gray-600 mt-1">
                            Contains {(selectedClass as any).events.length} event{(selectedClass as any).events.length !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">New Start Date *</label>
                      <input
                        type="date"
                        className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                        value={targetStartDate}
                        onChange={(e) => setTargetStartDate(e.target.value)}
                        required
                      />
                      {targetStartDate && (() => {
                        const date = new Date(targetStartDate)
                        const weekday = date.toLocaleDateString('en-US', { weekday: 'long' })
                        return (
                          <div className="text-xs text-gray-600 mt-1">
                            Selected: <strong>{weekday}</strong> - Events will be adjusted to start from this date, maintaining the same pattern. Holiday weeks will be skipped.
                          </div>
                        )
                      })()}
                      {!targetStartDate && (
                        <div className="text-xs text-gray-600 mt-1">
                          Events will be adjusted to start from this date, maintaining the same pattern. Holiday weeks will be skipped.
                        </div>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <button className="btn-secondary" onClick={() => { setSelectedClass(null); setTargetStartDate('') }}>Cancel</button>
                      <button
                        className="btn-primary"
                        onClick={() => handleImport(selectedClass as any)}
                        disabled={!targetStartDate || importing}
                        title="Load this class as a template. All events will be recreated starting from your selected date, maintaining the same weekly pattern."
                      >
                        {importing ? 'Loading as Template...' : 'Load as New Class Template'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

